import React from "react";
import { useForm, FormProvider, FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardBody, CardFooter, Button, Divider, Alert, addToast } from "@heroui/react";
import { Icon } from "@iconify/react";
import { PersonalInfoForm } from "../../admin/AdminCreateRequest/PersonalInfoForm";
import { MedicalQuestionsForm } from "../../admin/AdminCreateRequest/MedicalQuestionsForm";
import { DosageForm } from "../../admin/AdminCreateRequest/DosageForm";
import { DocumentsUploadForm } from "../../admin/AdminCreateRequest/DocumentsUploadForm";
import { TransportersForm } from "../../admin/AdminCreateRequest/TransportersForm";
import { SubmissionDeclaration } from "../SubmissionDeclaration/SubmissionDeclaration";
import { useSubmissionDeclaration } from "../SubmissionDeclaration/useSubmissionDeclaration";
import { RequestSchema, TRequest } from "@shared/entities";
import { useAppApi } from "src/appApi";
import { useStore } from "src/store/store";
import { usePrefillClientDetails } from "src/hooks/usePrefillClientDetails";
import { analytics } from "src/infra/analytics/analytics";
import { scrollToFirstError } from "src/components/forms/scrollToFirstError";

type Step = {
	fields: Array<FieldPath<TRequest>>;
};
const steps: Array<Step> = [
	{ fields: ["clientDetails", "requestType", "reasonType", "isRecommendationOnly"] },
	{
		fields: ["medicalQuestionnaire"],
	},
	{
		fields: ["dosages", "existsDosage", "userComment"],
	},
	{
		fields: ["documents"],
	},
	{
		fields: ["transporters"],
	},
] as const;

export default function ClientCreateRequest() {
	const store = useStore();
	const appApi = useAppApi();

	const methods = useForm<TRequest>({
		resolver: zodResolver(RequestSchema),
		mode: "all",
		reValidateMode: "onChange",
		shouldUnregister: false,
		defaultValues: {
			id: crypto.randomUUID(),
			status: "pending", // Start as draft, will be pending when completed
			userId: store.user?.uid || "",
			createdBy: "client",
			createdAt: Date.now(),
			isRecommendationOnly: false,
			// Pre-fill with user's profile data if available
			clientDetails: {
				firstName: store.profile?.firstName || "",
				lastName: store.profile?.lastName || "",
				email: store.profile?.email || "",
				phone: store.profile?.phone || "",
				phone2: store.profile?.phone2 || "",
				IDNumber: store.profile?.IDNumber || "",
				dob: store.profile?.dob || "",
				sex: store.profile?.sex || undefined,
				healthInsurance: store.profile?.healthInsurance || undefined,
				city: store.profile?.city || "",
				street: store.profile?.street || "",
				houseNumber: store.profile?.houseNumber || "",
				apartmentNumber: store.profile?.apartmentNumber || "",
			} as any,
		},
	});

	usePrefillClientDetails(methods);
	const declaration = useSubmissionDeclaration();

	// Sending is several server calls (declaration, request, profile): show it
	// as loading and refuse a second press while it runs.
	const [isSending, setIsSending] = React.useState(false);

	const [currentStep, setCurrentStep] = React.useState(1);
	const totalSteps = steps.length;
	const isLastStep = currentStep >= totalSteps;

	// Get active requests from store (fetched once on login)
	const activeRequests = store.activeRequests;
	const hasActiveRequest = activeRequests.length > 0;
	const isAdmin = store.user?.admin;

		// A request the client started and did not submit can be continued. A
	// submitted one cannot be reopened here: payment is no longer part of this
	// form (an admin sends a link), so "pending and unpaid" is just submitted.
	const draftRequest = activeRequests.find((request) => request.status === "draft");

	// Load the draft to continue it
	React.useEffect(() => {
		if (draftRequest && !isAdmin) {
			// Reset form with existing request data
			methods.reset(draftRequest);
		}
	}, [draftRequest, isAdmin, methods]);

	// Don't allow clients to create new requests if they have active requests (unless it's a draft)
	if (isAdmin) {
		// Continue with normal flow for admin users
	} else if (hasActiveRequest && !draftRequest) {
		// A client with a submitted request waits for it to finish before opening another
		return (
			<div className="min-h-screen bg-gray-50 p-4 md:p-6">
				<div className="max-w-4xl mx-auto">
					<Card className="mb-6">
						<CardHeader className="flex flex-col gap-1">
							<h1 className="text-2xl font-bold">יצירת בקשה חדשה</h1>
							<p className="text-default-500">מילוי פרטי בקשה לרישיון קנאביס רפואי</p>
						</CardHeader>
					</Card>

					<Alert
						color="warning"
						variant="faded"
						title="לא ניתן ליצור בקשה חדשה"
						description="יש לך בקשה פעילה בתהליך. לא ניתן ליצור בקשה נוספת עד להשלמת הבקשה הנוכחית."
						startContent={<Icon icon="lucide:alert-triangle" className="h-5 w-5" />}
						className="mb-6"
					/>

					<Card>
						<CardBody>
							<div className="text-center py-8">
								<p className="text-default-500 mb-4">
									יש לך {activeRequests.length} בקשה{activeRequests.length > 1 ? "ות" : ""}{" "}
									פעילה{activeRequests.length > 1 ? "ות" : ""} בתהליך
								</p>
								<Button
									color="primary"
									variant="flat"
									onPress={() => appApi.navigate("/my_profile")}
									startContent={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
								>
									חזור לפרופיל
								</Button>
							</div>
						</CardBody>
					</Card>
				</div>
			</div>
		);
	}

	const showStepErrors = (title: string, description: string) => {
		appApi.api.system.uiLogs({
			message: "new request step invalid",
			severity: "WARNING",
			step: currentStep,
			fields: Object.keys(methods.formState.errors),
		});
		addToast({ title, description, color: "danger" });
		scrollToFirstError();
	};

	const nextStep = async () => {
		if (isSending) return;
		const stepDetails = steps[currentStep - 1];

		if (!stepDetails) return;

		// Only this step's fields decide whether it can move on. An error left
		// on another step's field must not block it silently; the full check
		// before sending catches anything really missing.
		const isValid = await methods.trigger(stepDetails.fields, { shouldFocus: true });
		if (!isValid) {
			// On the last step, mark the declaration too, so every problem shows at once.
			if (isLastStep) declaration.markIfMissing({ scroll: false });
			showStepErrors("יש שדות שצריך להשלים", "השדות המסומנים באדום חסרים או לא תקינים.");
			return;
		}

		// For step 1, the email is required unless this is a recommendation only
		if (currentStep === 1) {
			const formValues = methods.getValues();
			if (!formValues.isRecommendationOnly && !formValues.clientDetails?.email) {
				methods.setError("clientDetails.email", {
					type: "manual",
					message: "אמייל נדרש עבור בקשות שאינן להמלצה בלבד",
				});
				showStepErrors("חסר אימייל", "יש למלא כתובת אימייל כדי להמשיך.");
				return;
			}
		}

		if (currentStep === 1) {
			analytics.track("CLIENT_SELECT_REQUEST_PERSONAL_DETAILS", methods.getValues());
		}

		if (currentStep === 2) {
			analytics.track("CLIENT_SELECT_REQUEST_MEDICAL_QUESTIONS", methods.getValues());
		}
		if (currentStep === 3) {
			analytics.track("CLIENT_SELECT_REQUEST_DOSAGE", methods.getValues());
		}
		if (currentStep === 4) {
			analytics.track("CLIENT_SELECT_REQUEST_DOCUMENTS", methods.getValues());
		}
		if (isLastStep) {
			await onSubmit(methods.getValues());
			return;
		}

		setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
	};

	const onSubmit = async (data: TRequest) => {
		setIsSending(true);
		try {
			await submit(data);
		} finally {
			setIsSending(false);
		}
	};

	const submit = async (data: TRequest) => {
		const validation = RequestSchema.safeParse(data);

		if (!validation.success) {
			appApi.api.system.uiLogs({
				message: "submit request error",
				severity: "ERROR",
				errors: validation.error,
			});
			addToast({
				title: "לא ניתן לשלוח את הבקשה",
				description: "חסרים פרטים בבקשה. חזור/י לשלבים הקודמים ובדוק/י שכל השדות מולאו.",
				color: "danger",
			});
			return;
		}

		// The patient declaration: required, and recorded by the server first
		// (which also puts it on the request — the browser never writes it).
		const accepted = await declaration.confirm(data.id);
		if (!accepted) return;

		try {
			// Set status to pending when client completes the form
			const requestData = {
				...data,
				status: "pending" as const,
				userId: store.user?.uid || "",
			};

			const result = await appApi.api.client.setRequest(requestData);

			if (result.success) {
				// Update the store with the updated/new active request
				if (draftRequest && !isAdmin) {
					// Update existing request in store
					const updatedRequests = activeRequests.map((req) =>
						req.id === draftRequest.id ? { ...req, ...requestData } : req
					);
					store.storeApi.setActiveRequests(updatedRequests);
				} else {
					// Add new request to store
					store.storeApi.setActiveRequests([...activeRequests, requestData]);
				}

				if (store.profile) {
					// fill client details with profile data
					await appApi.api.client.updateProfile({
						...store.profile,
						...requestData.clientDetails,
					});
				}

				appApi.navigate("/my_profile");
			} else {
				console.error("Error saving request:", result);
				addToast({ title: "שליחת הבקשה נכשלה", description: "נסה/י שוב בעוד רגע.", color: "danger" });
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			addToast({ title: "שליחת הבקשה נכשלה", description: "נסה/י שוב בעוד רגע.", color: "danger" });
		}
	};

	const prevStep = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return <PersonalInfoForm />;
			case 2:
				return <MedicalQuestionsForm />;
			case 3:
				return <DosageForm />;
			case 4:
				return <DocumentsUploadForm />;
			case 5:
				return (
					<div className="space-y-6">
						<TransportersForm />
						<Divider />
						<SubmissionDeclaration
							accepted={declaration.accepted}
							onAcceptedChange={declaration.onAcceptedChange}
							renewalContactConsent={declaration.renewalContactConsent}
							onRenewalContactConsentChange={declaration.setRenewalContactConsent}
							showError={declaration.showError}
							errorAttempt={declaration.errorAttempt}
						/>
					</div>
				);
			default:
				return <PersonalInfoForm />;
		}
	};

	const getStepTitle = () => {
		switch (currentStep) {
			case 1:
				return "פרטים אישיים";
			case 2:
				return "שאלון רפואי";
			case 3:
				return "מינון מבוקש";
			case 4:
				return "העלאת מסמכים";
			case 5:
				return "פרטי משנעים";
			default:
				return "";
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 p-4 md:p-6">
			<div className="max-w-4xl mx-auto">
				<Card className="mb-6">
					<CardHeader className="flex flex-col gap-1">
						<h1 className="text-2xl font-bold">
							{draftRequest && !isAdmin ? "המשך בקשה" : "יצירת בקשה חדשה"}
						</h1>
						<p className="text-default-500">
							{draftRequest && !isAdmin
								? "השלמת פרטי בקשה לרישיון קנאביס רפואי"
								: "מילוי פרטי בקשה לרישיון קנאביס רפואי"}
						</p>
					</CardHeader>
				</Card>

				<div className="mb-6">
					<div className="flex justify-between items-center mb-2">
						<h2 className="text-lg font-semibold">{getStepTitle()}</h2>
						<div className="text-sm text-default-500">
							שלב {currentStep} מתוך {totalSteps}
						</div>
					</div>
					<div className="w-full bg-default-100 rounded-full h-2.5">
						<div
							className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${(currentStep / totalSteps) * 100}%` }}
						></div>
					</div>
				</div>

				<FormProvider {...methods}>
					<form>
						<Card className="mb-6">
							<CardBody>{renderStepContent()}</CardBody>
							<Divider />
							<CardFooter className="flex justify-between">
								<Button
									variant="flat"
									color="default"
									type="button"
									startContent={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
									onPress={prevStep}
									isDisabled={currentStep === 1}
								>
									הקודם
								</Button>

								<Button
									color={isLastStep ? "success" : "primary"}
									type={"button"}
									isLoading={isSending}
									onPress={nextStep}
									endContent={
										isLastStep ? (
											<Icon icon="lucide:check" className="h-4 w-4" />
										) : (
											<Icon icon="lucide:arrow-left" className="h-4 w-4" />
										)
									}
								>
									{isLastStep
										? draftRequest && !isAdmin
											? "עדכן בקשה"
											: "שלח בקשה"
										: "המשך"}
								</Button>
							</CardFooter>
						</Card>
					</form>
				</FormProvider>
			</div>
		</div>
	);
}
